import {
  Flex,
  FlexProps,
  Heading,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  Text,
  Box,
  useTheme,
} from '@chakra-ui/react';
import styled from '@emotion/styled/macro';
import { ReactComponent as MyChoooseText } from 'assets/svgs/my-chooose-text.svg';
import { ReactComponent as WeChoooseText } from 'assets/svgs/we-chooose-text.svg';
import Button from 'components/Button/Button';
import useBreakpoint from 'hooks/useBreakpoint';
import { isCorporateThemeSelector } from 'modules/app/appSlice';
import { setIsAuthenticated, signInOrSignUpGuest } from 'modules/auth/authSlice';
import { resetUser } from 'modules/user/userSlice';
import { rgba } from 'polished';
import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { RootState } from 'store';
import { rem } from 'utils/fonts';

const StyledWeChoooseText = styled(WeChoooseText)`
  margin-bottom: 10px;
`;

const StyledMyChoooseText = styled(MyChoooseText)<{ isCorporateTheme?: boolean }>`
  margin-bottom: 7px;
  width: ${({ isCorporateTheme }) => (isCorporateTheme ? '115px' : 'auto')};
`;

const StyledOverlay = styled(ModalOverlay)`
  backdrop-filter: blur(4px);
`;

const StyledPartnerName = styled(Text)`
  font-weight: 800;
  margin-right: 3px;
  text-overflow: ellipsis;
  overflow: hidden;
  white-space: nowrap;
`;

interface Props {
  onClose: () => void;
  isOpen: boolean;
}

const SignupChoiceBox: FC<FlexProps> = ({ children, ...props }) => {
  const theme = useTheme();
  const { isMobile } = useBreakpoint();
  return (
    <Flex w="100%" mt="10px" border={`1px solid ${rgba(theme.colors.gray[900], 0.1)}`} borderRadius="9px" {...props}>
      <Flex w="100%" direction={isMobile ? 'column' : 'row'} alignItems="center" p="35px 18px 35px 30px" justifyContent="space-between">
        {children}
      </Flex>
    </Flex>
  );
};

const ChooseContextModal: FC<Props> = ({ onClose, isOpen }) => {
  const theme = useTheme();
  const location = useLocation();
  const dispatch = useDispatch();
  const { t } = useTranslation(['signup', 'settings']);
  const isCorporateTheme = useSelector(isCorporateThemeSelector);
  const { partnershipId } = useSelector((state: RootState) => state.app);
  const partnerName = process.env.REACT_APP_PARTNER_NAME;
  const isProgramNameSet = t(`settings:programName.${partnershipId}`, 'null') !== 'null';
  const isTestEnv = process.env.REACT_APP_PORTAL_API_URL?.includes('chooose-portal-test');
  const myChoooseSignUpSignInPath = `guest-signin-signup/mychooose${location.search}`;
  const linkToMyChoooseGuestSignInSignUp = isTestEnv
    ? `https://portal-test.chooose.today/${myChoooseSignUpSignInPath}`
    : `https://portal.chooose.today/${myChoooseSignUpSignInPath}`;

  const goToSignUpWeChooose = (): void => {
    dispatch(setIsAuthenticated(false));
    dispatch(resetUser());
    dispatch(signInOrSignUpGuest(location.search + '&context=wechooose', 'wechooose'));
  };

  const goToSignUpMyChooose = (): void => {
    dispatch(setIsAuthenticated(false));
    dispatch(resetUser());
    dispatch(signInOrSignUpGuest(location.search + '&context=mychooose', 'mychooose'));
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose}>
      <StyledOverlay />
      <ModalContent maxW="590px" w="100%" borderRadius="15px">
        <ModalHeader p="48px 30px 0 30px">
          <Heading fontSize={rem(22)} fontFamily="heading" fontWeight="bold" textAlign="center">
            {t('signup:claimImpactBanner.modalGeneralHeader')}
          </Heading>
          <Text mt="20px" fontSize={rem(14)} fontWeight="500" textAlign="center" color={rgba(theme.colors.gray[900], 0.7)}>
            {t('signup:claimImpactBanner.modalGeneralDescription')}
          </Text>
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody p="20px 30px 30px 30px">
          <SignupChoiceBox>
            {isCorporateTheme ? (
              <Flex
                justify={{ base: 'center', md: 'flex-start' }}
                align="center"
                w="100%"
                mr="10px"
                sx={{ whiteSpace: 'nowrap' }}
                mb={{ base: '5px', md: 0 }}
              >
                <StyledPartnerName fontSize="mdMax" fontFamily="heading" maxWidth="130px" color="primary.500">
                  {t(`settings:programName.${partnershipId}`, partnerName || '')}
                </StyledPartnerName>
                {!isProgramNameSet && (
                  <Box as="span" fontFamily="heading" color="secondary.900" fontSize="lgMin" fontWeight={700} lineHeight={rem(15)}>
                    CARBON
                  </Box>
                )}
              </Flex>
            ) : (
              <StyledWeChoooseText />
            )}
            <Button fontSize={rem(15)} height="50px" minW="auto" variant="outline" onClick={goToSignUpWeChooose}>
              {t('signup:claimImpactBanner.modalSignupWeChoooseButton')}
            </Button>
          </SignupChoiceBox>
          <SignupChoiceBox>
            <StyledMyChoooseText isCorporateTheme={isCorporateTheme} />
            <a href={isCorporateTheme ? linkToMyChoooseGuestSignInSignUp : undefined}>
              <Button fontSize={rem(15)} height="50px" minW="auto" variant="outline" onClick={isCorporateTheme ? undefined : goToSignUpMyChooose}>
                {t('signup:claimImpactBanner.modalSignupMyChoooseButton')}
              </Button>
            </a>
          </SignupChoiceBox>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};

export default ChooseContextModal;
